/**
 * THE CRITICAL PATH — src/critical-path.ts
 *
 *   npx tsx scripts/test-critical-path.ts
 *
 * True CPM over the cards' finish-to-start dependencies ([[dependencies.ts]]) and
 * their working-day windows: a backward pass from the committed end gives every
 * dated, linked card its LATEST start/finish and its TOTAL FLOAT — the working days
 * it can slip before it moves the project end. Zero-float cards are the CRITICAL
 * path (the chain that sets the finish); a small positive float is NEAR-CRITICAL
 * (one knock from becoming critical). This is the number a PM actually steers by —
 * "which bar can slip, and by how long" — which an 80%-done bar alone can't answer.
 *
 * TWO HONESTY RULES this module is built around (pm-witness, 31 Oct):
 *   1. FLOAT IS MEASURED AGAINST A FROZEN END. The `committedEnd` is the plan's
 *      baseline Go-Live, never a moving forecast — float against a sliding anchor is
 *      meaningless. The caller passes the frozen baseline; with none, the latest
 *      planned due is used as the end.
 *   2. CPM ONLY SPANS THE DATED, LINKED SUBGRAPH. A card missing a start or a due
 *      has no window to place, so it is left OUT and reported in `coverage` — the
 *      surface says "covers N of M, K undated" rather than drawing a confident chain
 *      over cards it cannot see. Those undated cards are what Layer A's "needs
 *      attention" then flags, so the schedule vouches for its own trustworthiness.
 *
 * Pure — no store, no React, no second date-iterator (all working-day maths via
 * work-calendar.ts + the dependency graph). Proved by scripts/test-critical-path.ts.
 */
import { buildDepGraph, dependencyLinks, stepWorkingDays, type DepCard } from './dependencies';
import { addDays } from './sprints';
import {
  isWorkingDay,
  workingDaysInclusive,
  addWorkingDays,
  DEFAULT_CALENDAR,
  type WorkCalendar,
} from './work-calendar';

/** Float at or below this many working days, but above zero, is NEAR-CRITICAL — the
 *  early-warning set the plain critical path misses: a card that becomes critical on
 *  a small slip. Three working days ≈ a slip a single bad week produces. */
export const NEAR_CRITICAL_WORKING_DAYS = 3;

/** Whether a date field names a real day (mirrors dependencies.ts `hasDay`). */
function hasDay(d: string | null | undefined): d is string {
  return typeof d === 'string' && d.length > 0;
}

/** The day `n` WORKING days BEFORE `fromISO` — the backward analog of
 *  work-calendar's `addWorkingDays` (which only steps forward). `fromISO` itself is
 *  not counted; n ≤ 0 returns it unchanged. Bounded so a fully-closed calendar can't
 *  loop forever. Used only for the latest-start/finish backward pass. */
function subWorkingDays(fromISO: string, n: number, cal: WorkCalendar): string {
  if (n <= 0) return fromISO;
  let cur = fromISO;
  let left = n;
  for (let i = 0; i < 36600 && left > 0; i++) {
    cur = addDays(cur, -1);
    if (isWorkingDay(cur, cal)) left -= 1;
  }
  return cur;
}

export interface CpmNode {
  ref: string;
  startsOn: string;
  dueDate: string;
  /** The planned window's length in working days, inclusive (a one-day task = 1). */
  durationWorkingDays: number;
  /** The latest this card could start / finish without moving the project end. */
  latestStart: string;
  latestFinish: string;
  /** Working days the card can slip before it moves the end. ≤ 0 = on/over the line
   *  (critical); a positive value is its slack. Negative = already past its latest. */
  totalFloatWorkingDays: number;
  critical: boolean;
  nearCritical: boolean;
}

export interface CpmCoverage {
  /** Cards handed in. */
  total: number;
  /** Cards with BOTH a start and a due — the ones CPM could place. */
  scheduled: number;
  /** Refs left out because they are missing a start or a due. */
  undatedRefs: string[];
}

export interface CpmResult {
  /** The dated cards, by ref, with their float and latest dates. */
  nodes: Map<string, CpmNode>;
  /** The zero-float chain that drives the end, in schedule order. */
  criticalPath: string[];
  /** The frozen end float was measured against (the committed baseline, or the
   *  latest planned due when none was given). */
  projectEnd: string;
  coverage: CpmCoverage;
  /** True when every dated card is critical AND they form one simple chain — the
   *  honest degenerate case: there is nothing to parallelise, the "path" is just the
   *  list, and the only levers are to overlap steps or cut scope. */
  isStrictSequence: boolean;
}

/**
 * Compute the critical path over a set of cards.
 *
 * @param cards the cards to schedule (the caller's scope — e.g. a project's open
 *   cards). Finish-to-start links come from their `blockerRefs` (dependencies.ts).
 * @param committedEnd the FROZEN baseline Go-Live day (YYYY-MM-DD) float is measured
 *   against; null → the latest planned due among the dated cards is used.
 * @param cal the workspace working calendar (weekends + holidays).
 * @param nearCriticalWorkingDays the float threshold for "near-critical".
 */
export function criticalPath(
  cards: DepCard[],
  committedEnd: string | null = null,
  cal: WorkCalendar = DEFAULT_CALENDAR,
  nearCriticalWorkingDays = NEAR_CRITICAL_WORKING_DAYS,
): CpmResult {
  // The dated, linked subgraph: only cards with a real window can be placed.
  const dated = cards.filter((c) => hasDay(c.startsOn) && hasDay(c.dueDate));
  const undatedRefs = cards.filter((c) => !(hasDay(c.startsOn) && hasDay(c.dueDate))).map((c) => c.ref);
  const coverage: CpmCoverage = { total: cards.length, scheduled: dated.length, undatedRefs };

  const empty: CpmResult = {
    nodes: new Map(),
    criticalPath: [],
    projectEnd: committedEnd ?? '',
    coverage,
    isStrictSequence: false,
  };
  if (dated.length === 0) return empty;

  const graph = buildDepGraph(dated); // edges only BETWEEN dated cards (present-subset)
  const byRef = new Map(dated.map((c) => [c.ref, c] as const));

  // The end float is measured against: the frozen committed baseline, else the
  // latest planned due in the set (so float still means something with no baseline).
  let projectEnd = committedEnd;
  if (!hasDay(projectEnd)) {
    projectEnd = dated.reduce((m, c) => (c.dueDate! > m ? c.dueDate! : m), dated[0].dueDate!);
  }

  const duration = (c: DepCard): number =>
    Math.max(1, workingDaysInclusive(c.startsOn!, c.dueDate!, cal));

  // REVERSE-TOPOLOGICAL ORDER — process a card only after every card that depends on
  // it (which constrains its latest finish) is final. Kahn's over the dependents
  // edges: a node is ready when it has no unprocessed dated dependent.
  const outdeg = new Map<string, number>();
  for (const ref of graph.refs) outdeg.set(ref, graph.dependents(ref).length);
  const ready = graph.refs.filter((ref) => (outdeg.get(ref) ?? 0) === 0);
  const order: string[] = [];
  while (ready.length) {
    const ref = ready.shift()!;
    order.push(ref);
    for (const p of graph.predecessors(ref)) {
      const n = (outdeg.get(p) ?? 0) - 1;
      outdeg.set(p, n);
      if (n === 0) ready.push(p);
    }
  }
  // Any ref not ordered sits on a dependency cycle (the data model refuses to write
  // one — wouldCycle — but be safe): give it no successor constraint so it still
  // lands against the end rather than being dropped.
  for (const ref of graph.refs) if (!order.includes(ref)) order.push(ref);

  // BACKWARD PASS — latest finish, latest start, total float.
  //
  // Each dependent D imposes a ceiling on its predecessor P's latest finish, read
  // from the TYPE and LAG of the D→P link — the exact inverse of the floors the
  // cascade lays down forward (dependencies.ts `cascadeShifts`), so the two engines
  // can never disagree about what a link means:
  //   · FS  D.start ≥ P.finish + 1 + lag   → P.finish ≤ D.latestStart  stepped back (lag+1)
  //   · FF  D.finish ≥ P.finish + lag       → P.finish ≤ D.latestFinish stepped back lag
  //   · SS  D.start ≥ P.start + lag          → P.start  ≤ D.latestStart  stepped back lag
  //   · SF  D.finish ≥ P.start + lag         → P.start  ≤ D.latestFinish stepped back lag
  // The two START-anchored types (SS/SF) cap P's start, so they are turned into a
  // finish ceiling by adding P's own working duration. A card with no dated
  // dependent finishes by the project end. Before this, every edge was read as a
  // plain FS with no lag, so a SS/FF/SF or lagged link quietly computed the wrong
  // float — the critical path ignored exactly the link detail the card modal lets
  // you set.
  const latestFinish = new Map<string, string>();
  const latestStart = new Map<string, string>();
  for (const ref of order) {
    const durP = duration(byRef.get(ref)!);
    let lf = projectEnd!;
    for (const d of graph.dependents(ref)) {
      const ls = latestStart.get(d);
      const lfin = latestFinish.get(d);
      if (!ls || !lfin) continue; // a dependent on a cycle, not yet placed — skip its constraint
      const link = dependencyLinks(byRef.get(d)!).find((l) => l.ref === ref);
      const type = link?.type ?? 'FS';
      const lag = link?.lag ?? 0;
      let ceil: string;
      switch (type) {
        case 'FS': ceil = stepWorkingDays(ls, -(lag + 1), cal); break;
        case 'FF': ceil = stepWorkingDays(lfin, -lag, cal); break;
        // Start-anchored: cap P's START, then add its duration back to get the finish.
        case 'SS': ceil = addWorkingDays(stepWorkingDays(ls, -lag, cal), durP - 1, cal); break;
        case 'SF': ceil = addWorkingDays(stepWorkingDays(lfin, -lag, cal), durP - 1, cal); break;
      }
      if (ceil < lf) lf = ceil;
    }
    latestFinish.set(ref, lf);
    latestStart.set(ref, subWorkingDays(lf, durP - 1, cal));
  }

  // FLOAT + the node records. Float is the signed working-day gap between the planned
  // due and the latest finish: due == LF → 0; due before LF → positive slack; due
  // after LF → negative (already past the line it needs to hold).
  const nodes = new Map<string, CpmNode>();
  for (const c of dated) {
    const due = c.dueDate!;
    const lf = latestFinish.get(c.ref)!;
    const float =
      lf >= due
        ? workingDaysInclusive(due, lf, cal) - 1
        : -(workingDaysInclusive(lf, due, cal) - 1);
    const critical = float <= 0;
    nodes.set(c.ref, {
      ref: c.ref,
      startsOn: c.startsOn!,
      dueDate: due,
      durationWorkingDays: duration(c),
      latestStart: latestStart.get(c.ref)!,
      latestFinish: lf,
      totalFloatWorkingDays: float,
      critical,
      nearCritical: !critical && float <= nearCriticalWorkingDays,
    });
  }

  // THE CRITICAL PATH — the zero-float cards in schedule order (by planned start,
  // then the latest start as a tiebreak). They form the chain that sets the end.
  const criticalPathRefs = [...nodes.values()]
    .filter((n) => n.critical)
    .sort((a, b) => (a.startsOn < b.startsOn ? -1 : a.startsOn > b.startsOn ? 1 : a.latestStart < b.latestStart ? -1 : 1))
    .map((n) => n.ref);

  // THE DEGENERATE CASE — every dated card critical AND one simple chain (each card
  // has at most one dated predecessor and at most one dated dependent). Then there is
  // no float to manage and nothing to parallelise; the path is just the list.
  const everyCritical = nodes.size > 1 && [...nodes.values()].every((n) => n.critical);
  const simpleChain = graph.refs.every(
    (ref) => graph.predecessors(ref).length <= 1 && graph.dependents(ref).length <= 1,
  );
  const isStrictSequence = everyCritical && simpleChain;

  return { nodes, criticalPath: criticalPathRefs, projectEnd: projectEnd!, coverage, isStrictSequence };
}
